'use client'

import * as Sentry from '@sentry/nextjs'
import { AlertCircle, Eye, EyeOff, Loader2 } from 'lucide-react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useEffect,useState } from 'react'
import { useFormStatus } from 'react-dom'
import { useForm } from 'react-hook-form'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import { useAuth } from '@/contexts/AuthContext'
import kratos from '@/utils/ory-client'

export default function LoginForm({
  searchParams
}: {
  searchParams: Promise<{ flow?: string; from?: string; status?: string; return_to?: string; invite_email?: string }>
}) {
  const [resolvedSearchParams, setResolvedSearchParams] = useState<{
    flow?: string
    from?: string
    status?: string
    return_to?: string
    invite_email?: string
  }>({})
  const [loginFlow, setLoginFlow] = useState<any>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)
  const [flowMessages, setFlowMessages] = useState<any[]>([])
  const [flowState, setFlowState] = useState<string>('')
  const [showPassword, setShowPassword] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const router = useRouter()
  const { refreshSession } = useAuth()
  const safeReturnTo =
    typeof resolvedSearchParams.return_to === 'string' && resolvedSearchParams.return_to.startsWith('/')
      ? resolvedSearchParams.return_to
      : undefined
  const inviteEmailHint =
    typeof resolvedSearchParams.invite_email === 'string' ? resolvedSearchParams.invite_email.trim() : ''

  // Resolve searchParams on mount
  useEffect(() => {
    const resolveSearchParams = async () => {
      const resolved = await searchParams
      setResolvedSearchParams(resolved)
    }
    resolveSearchParams()
  }, [searchParams])

  const form = useForm({
    defaultValues: {
      email: '',
      password: ''
    }
  })

  // Google OIDC login function
  const startOidcLogin = async () => {
    try {
      setIsGoogleLoading(true)
      setErrorMessage(null)
      
      // 1) Start a browser login flow — returnTo callback so we can record login metric after OIDC redirect
      const { data: flow } = await kratos.createBrowserLoginFlow({
        returnTo: safeReturnTo ? `${window.location.origin}${safeReturnTo}` : `${window.location.origin}/auth/callback`
      })

      // Get CSRF token from the flow
      const csrfToken = (flow.ui.nodes as any).find(
        (node: any) => node.attributes.name === 'csrf_token'
      )?.attributes?.value

      if (!csrfToken) {
        setErrorMessage('Security token missing. Please refresh the page.')
        return
      }

      // 2) Submit the flow using OIDC (google). Kratos will respond with a redirect url.
      try {
        const res = await kratos.updateLoginFlow({
          flow: flow.id,
          updateLoginFlowBody: {
            method: 'oidc',
            provider: 'google',
            // csrf_token not needed for OIDC on browser flows
          },
        })

        // Some setups may return 200 with the URL:
        const okUrl = (res.data as any)?.redirect_browser_to
        if (okUrl) {
          window.location.href = okUrl
          return
        }

        // Fallthrough — handled in catch
      } catch (err: any) {
        const url = err?.response?.data?.redirect_browser_to
        if (err?.response?.status === 422 && url) {
          window.location.href = url   // <-- the expected path
          return
        }
        console.error('Flow Ory Error:', err.response || err)
        setErrorMessage(
          err?.response?.data?.error?.message ||
            'Failed to start Google login. Please try again.'
        )
      }
    } catch (error: any) {
      // Handle 422 response which contains the redirect URL
      if (error.response?.status === 422 && error.response?.data?.ui?.messages) {
        const redirectMessage = error.response.data.ui.messages.find((msg: any) => 
          msg.type === 'error' && msg.text.includes('browser location change required')
        )
        
        if (redirectMessage) {
          // Extract the URL from the error message
          const urlMatch = redirectMessage.text.match(/https:\/\/[^\s]+/)
          if (urlMatch) {
            window.location.href = urlMatch[0]
            return
          }
        }
      }
      
      // If we can't extract the redirect URL, log the error
      console.error('Google OIDC login failed:', error.response || error)
      setErrorMessage(
        error.response?.data?.error?.message ||
        'Failed to start Google login. Please try again.'
      )
    } finally {
      setIsGoogleLoading(false)
    }
  }

  useEffect(() => {
    async function initializeLoginFlow() {
      try {
        const flowId = resolvedSearchParams.flow
        let flow

        if (flowId) {
          flow = await kratos.getLoginFlow(
            {
              id: flowId
            },
            {
              withCredentials: true,
              headers: {
                Accept: 'application/json'
              }
            }
          )
          
          // Extract flow messages and state
          if (flow.data.ui?.messages) {
            setFlowMessages(flow.data.ui.messages)
          }
          if (flow.data.state) {
            setFlowState(flow.data.state)
          }
          
          // Pre-fill email if available in the flow
          const emailNode = flow.data.ui.nodes.find((node: any) => 
            node.attributes.name === 'identifier'
          )
          if (emailNode?.attributes && 'value' in emailNode.attributes) {
            form.setValue('email', (emailNode.attributes as any).value)
          }
        } else {
          flow = await kratos.createBrowserLoginFlow(
            {
              returnTo: safeReturnTo ? `${window.location.origin}${safeReturnTo}` : `${window.location.origin}/`
            },
            {
              withCredentials: true,
              headers: {
                Accept: 'application/json'
              }
            }
          )
        }

        setLoginFlow(flow.data)
      } catch (error: any) {
        console.error(
          'Failed to initialize login flow:',
          error.response || error
        )
        setErrorMessage(
          error.response?.data?.error?.message ||
            'Failed to initialize login. Please try again.'
        )
      }
    }

    initializeLoginFlow()
  }, [resolvedSearchParams.flow, form, safeReturnTo])

  const onSubmit = async (formData: any) => {
    if (!loginFlow) return

    const csrfToken = loginFlow.ui.nodes.find(
      (node: any) => node.attributes.name === 'csrf_token'
    )?.attributes.value

    if (!csrfToken) {
      setErrorMessage('Security token missing. Please refresh the page.')
      return
    }

    try {
      setIsSubmitting(true)
      // Clear any existing error messages
      setErrorMessage(null)
      
      const { data: _data } = await kratos.updateLoginFlow(
        {
          flow: loginFlow.id,
          updateLoginFlowBody: {
            method: 'password',
            identifier: formData.email,
            password: formData.password,
            csrf_token: csrfToken
          }
        },
        {
          withCredentials: true,
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          }
        }
      )

      // If we get here, the login was successful
      // Clear form data after successful submission
      form.reset()

      // Refresh auth state, record login metric with email, then redirect
      const user = await refreshSession()
      const email = user?.traits?.email != null ? String(user.traits.email).trim() || undefined : undefined
      if (email != null) {
        try {
          if (typeof Sentry.metrics?.count === 'function') {
            console.debug('[Sentry] Sending user_login metric', { email })
            Sentry.metrics.count('user_login', 1, { attributes: { email } })
            await Sentry.flush(2000)
            console.debug('[Sentry] user_login metric sent and flushed')
          } else {
            console.warn('[Sentry] metrics.count not available (SDK may need upgrade for metrics)')
          }
        } catch (metricErr) {
          console.error('[Sentry] Failed to send login metric', metricErr)
        }
      }
      const base = loginFlow.return_to || '/'
      const url = base.startsWith('http') ? new URL(base) : new URL(base, window.location.origin)
      url.searchParams.set('fl', '1')
      window.location.href = url.toString()
    } catch (err: any) {
      console.error('Login failed:', err.response || err)
      
      // Check if this is a flow error with messages
      if (err.response?.data?.ui?.messages) {
        setFlowMessages(err.response.data.ui.messages)
      }
      
      setErrorMessage(
        err.response?.data?.error?.message ||
          'Login failed. Please check your credentials.'
      )
      setIsSubmitting(false)
    }
  }

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    form.handleSubmit(onSubmit)()
  }

  return (
    <div className="light space-y-6" style={{ colorScheme: 'light' }}>
      <div className="text-center mb-6">
        <h1 className="text-3xl text-gray-900 mb-2">
          {flowState === 'choose_method' && flowMessages.some((msg: any) => 
            msg.context?.duplicate_identifier
          ) 
            ? 'Link Your Google Account' 
            : 'Log In'
          }
        </h1>
        {flowState === 'choose_method' && flowMessages.some((msg: any) => 
          msg.context?.duplicate_identifier
        ) && (
          <p className="text-gray-600 text-sm">
            Sign in with your password to link your Google account
          </p>
        )}
      </div>
      
      <div className="space-y-6">
        {/* Social Login Buttons */}
        <div className="grid gap-3">
          <Button 
            variant="outline" 
            className="w-full border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:text-black hover:shadow-lg transition-all duration-200"
            onClick={() => startOidcLogin()}
            disabled={isGoogleLoading}
            type="button"
          >
            {isGoogleLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Connecting to Google...
              </>
            ) : (
              <>
                <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
                Continue with Google
              </>
            )}
          </Button>
        </div>

        {/* Separator */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <Separator className="w-full bg-gray-300" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-white px-2 text-gray-500">
              Or continue with
            </span>
          </div>
        </div>

        <Form {...form}>
          <form 
            onSubmit={handleFormSubmit} 
            method="POST"
            className="space-y-6"
            noValidate
          >
            {resolvedSearchParams.from === 'signup' &&
              resolvedSearchParams.status === 'success' && (
                <Alert className="bg-green-50 border-green-200 text-green-800">
                  <AlertTitle className="text-green-800">Welcome to Fastfold AI</AlertTitle>
                  <AlertDescription className="text-green-700">
                    Your account was created successfully, you can now login
                  </AlertDescription>
                </Alert>
              )}

            {inviteEmailHint ? (
              <Alert className="bg-blue-50 border-blue-200 text-blue-800">
                <AlertTitle>Team Invite</AlertTitle>
                <AlertDescription>
                  Login or create an account with <span className="font-medium">{inviteEmailHint}</span> to accept
                  the invite.
                </AlertDescription>
              </Alert>
            ) : null}

            {errorMessage && (
              <Alert variant="destructive" className="bg-red-50 border-red-200 text-red-800">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
            )}

            {/* Display flow messages from OAuth flow */}
            {flowMessages.length > 0 && (
              <Alert className="bg-blue-50 border-blue-200 text-blue-800">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Account Information</AlertTitle>
                <AlertDescription>
                  {flowMessages.map((message, index) => (
                    <div key={index} className="mb-2 last:mb-0">
                      {message.text}
                    </div>
                  ))}
                </AlertDescription>
              </Alert>
            )}




            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-gray-900">Email</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter your email address"
                      type="email"
                      required
                      autoComplete="email"
                      {...field}
                      className="border-gray-300 bg-white text-gray-900 placeholder:text-gray-500"
                      suppressHydrationWarning
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-gray-900">Password</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        placeholder="Enter password"
                        type={showPassword ? "text" : "password"}
                        required
                        minLength={6}
                        autoComplete="current-password"
                        {...field}
                        className="border-gray-300 bg-white text-gray-900 placeholder:text-gray-500 pr-10"
                        suppressHydrationWarning
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <LoginButton isSubmitting={isSubmitting} />

          </form>
        </Form>
      </div>
      
      <div className="flex flex-col items-center gap-2">
        <Link
          href="/password-reset"
          className="text-sm text-blue-600 hover:text-blue-700"
        >
          Forgot your password?
        </Link>
      </div>
      
      <div className="flex flex-col items-center gap-2">
        <Link
          href="/signup"
          className="text-sm text-blue-600 hover:text-blue-700"
        >
          Don&apos;t have an account? Sign up
        </Link>
      </div>
    </div>
  )
}
function LoginButton({ isSubmitting = false }: { isSubmitting?: boolean }) {
  const { pending } = useFormStatus()
  const loading = isSubmitting || pending

  return (
    <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white" disabled={loading}>
      {loading ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Logging in...
        </>
      ) : (
        'Log In'
      )}
    </Button>
  )
}
